/**
 * HTTP client for the Product Catalog Control Center AI chatbot (Brisque AI) backend.
 *
 * Mirrors the structure of `chatbotReportsApi.ts` (a small fetch wrapper that
 * injects auth + reads a base URL from env) but talks to a DIFFERENT backend and
 * is kept fully isolated from the reports chatbot — it has its own env var
 * (REACT_APP_PC_CHATBOT_API_ENDPOINT) and its own identity headers per the
 * Brisque AI integration guide (§1).
 *
 * Identity rule: there is no login call. Every request carries the caller identity
 * as headers: x-location-id, x-cc-user-id, x-session-id and Authorization: Bearer.
 */
import { authFetch } from './authFetch';
import { CREDENTIALS } from '../constants';
import type {
    PCChatbotIdentity,
    PCConversation,
    PCConversationListResponse,
    PCMessagesResponse,
    PCRenameResponse,
    PCDeleteResponse,
    PCConfirmResponse,
    PCCardResponse,
    PCChannelsResponse,
    PCSyncCardResponse,
    PCSyncResultRow,
    PCLogResponse,
    PCUndoResponse,
    PCCatalogueRefreshResponse,
    PCCtaResponse,
    PCHealthResponse,
    PCDuration,
} from '../interface/ProductCatalogChatbotInterface';

const PC_CHATBOT_BASE_URL = (process.env.REACT_APP_PC_CHATBOT_API_ENDPOINT || '').replace(/\/+$/, '');
const API_PREFIX = '/api/v1';
const SESSION_STORAGE_KEY = 'pc_chatbot_session_id';

/** Resolve the bearer token — prefer an explicitly passed one, else read the stored credentials. */
function getChatbotToken(passedToken?: string | null): string | null {
    if (passedToken && typeof passedToken === 'string' && passedToken.trim()) return passedToken.trim();
    try {
        const raw = localStorage.getItem(CREDENTIALS);
        if (!raw) return null;
        const stored = JSON.parse(raw);
        const cred = stored?.credentials ?? stored;
        const t =
            cred?.accessToken ??
            cred?.access_token ??
            cred?.token ??
            cred?.idToken ??
            cred?.id_token ??
            cred?.jwtToken ??
            cred?.jwt;
        return t && typeof t === 'string' && t.trim() ? t.trim() : null;
    } catch {
        return null;
    }
}

/** A stable per-tab session id (§3). Cached in sessionStorage; created on first use. */
export function getPCChatbotSessionId(): string {
    try {
        let sid = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (!sid) {
            sid =
                typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                    ? crypto.randomUUID()
                    : `sess-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
            sessionStorage.setItem(SESSION_STORAGE_KEY, sid);
        }
        return sid;
    } catch {
        return `sess-${Date.now().toString(36)}`;
    }
}

function buildHeaders(identity: PCChatbotIdentity, hasBody: boolean): Record<string, string> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (hasBody) headers['Content-Type'] = 'application/json';
    if (identity.locationId) headers['x-location-id'] = identity.locationId;
    if (identity.ccUserId) headers['x-cc-user-id'] = identity.ccUserId;
    const sessionId = identity.sessionId || getPCChatbotSessionId();
    if (sessionId) headers['x-session-id'] = sessionId;
    const token = getChatbotToken(identity.token);
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
}

/** An error thrown by the chatbot API — carries the HTTP status for callers to branch on (§2). */
export interface PCChatbotError extends Error {
    status?: number;
}

interface RequestOptions {
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
    identity: PCChatbotIdentity;
    body?: unknown;
}

async function pcChatbotFetch<T>(path: string, options: RequestOptions): Promise<T> {
    const hasBody = options.body !== undefined && options.body !== null;
    const headers = buildHeaders(options.identity, hasBody);

    const fetchOptions: RequestInit = { method: options.method, headers };
    if (hasBody) fetchOptions.body = JSON.stringify(options.body);

    const url = `${PC_CHATBOT_BASE_URL}${API_PREFIX}${path}`;
    const response = await authFetch(url, fetchOptions);

    if (!response.ok) {
        const errText = await response.text().catch(() => '');
        console.error('[productCatalogChatbotApi] ERROR =>', {
            url,
            status: response.status,
            errText: errText?.substring(0, 200),
        });
        let errMsg = 'Failed to reach Product Catalog AI';
        try {
            const parsed = JSON.parse(errText);
            errMsg = parsed?.error || parsed?.message || errMsg;
        } catch {
            /* keep default */
        }
        if (response.status === 401) errMsg = errMsg || 'Session expired. Please sign in again.';
        if (response.status === 403) errMsg = errMsg || 'That resource belongs to another user or location.';
        const err: PCChatbotError = new Error(errMsg);
        err.status = response.status;
        throw err;
    }

    if (response.status === 204) return null as unknown as T;
    return (await response.json()) as T;
}

/* ─────────────────────────── Health (§2, no auth) ─────────────────────────── */

export const pcHealthApi = async (): Promise<PCHealthResponse> => {
    const response = await authFetch(`${PC_CHATBOT_BASE_URL}/health`, { method: 'GET' });
    return response.json();
};

/* ─────────────────────────── Conversations (§4.1–4.6) ─────────────────────────── */

// POST /conversations
export const createConversationApi = (identity: PCChatbotIdentity, body?: { fresh?: boolean; title?: string }) =>
    pcChatbotFetch<PCConversation>('/conversations', { method: 'POST', identity, body: body ?? {} });

// GET /conversations
export const listConversationsApi = (identity: PCChatbotIdentity) =>
    pcChatbotFetch<PCConversationListResponse>('/conversations', { method: 'GET', identity });

// GET /conversations/:id
export const getConversationApi = (identity: PCChatbotIdentity, conversationId: string) =>
    pcChatbotFetch<PCConversation>(`/conversations/${conversationId}`, { method: 'GET', identity });

// POST /conversations/:id/messages
export const sendMessageApi = (identity: PCChatbotIdentity, conversationId: string, text: string) =>
    pcChatbotFetch<PCMessagesResponse>(`/conversations/${conversationId}/messages`, {
        method: 'POST',
        identity,
        body: { text },
    });

// PATCH /conversations/:id
export const renameConversationApi = (identity: PCChatbotIdentity, conversationId: string, title: string) =>
    pcChatbotFetch<PCRenameResponse>(`/conversations/${conversationId}`, {
        method: 'PATCH',
        identity,
        body: { title },
    });

// DELETE /conversations/:id
export const deleteConversationApi = (identity: PCChatbotIdentity, conversationId: string) =>
    pcChatbotFetch<PCDeleteResponse>(`/conversations/${conversationId}`, { method: 'DELETE', identity });

/* ─────────────────────────── Card actions (§4.7 / §6) ─────────────────────────── */

// POST /cards/:cardId/confirm
export const confirmCardApi = (identity: PCChatbotIdentity, cardId: string) =>
    pcChatbotFetch<PCConfirmResponse>(`/cards/${cardId}/confirm`, { method: 'POST', identity, body: {} });

// POST /cards/:cardId/cancel
export const cancelCardApi = (identity: PCChatbotIdentity, cardId: string) =>
    pcChatbotFetch<PCCardResponse>(`/cards/${cardId}/cancel`, { method: 'POST', identity, body: {} });

// POST /cards/:cardId/pick-candidate
export const pickCandidateApi = (identity: PCChatbotIdentity, cardId: string, candidateId: string) =>
    pcChatbotFetch<PCCardResponse>(`/cards/${cardId}/pick-candidate`, {
        method: 'POST',
        identity,
        body: { candidateId },
    });

// PATCH /cards/:cardId/channels
export const setCardChannelsApi = (identity: PCChatbotIdentity, cardId: string, rowId: string, channels: string[]) =>
    pcChatbotFetch<PCCardResponse>(`/cards/${cardId}/channels`, {
        method: 'PATCH',
        identity,
        body: { rowId, channels },
    });

// PATCH /cards/:cardId/duration
export const setCardDurationApi = (identity: PCChatbotIdentity, cardId: string, rowId: string, duration: PCDuration) =>
    pcChatbotFetch<PCCardResponse>(`/cards/${cardId}/duration`, {
        method: 'PATCH',
        identity,
        body: { rowId, duration },
    });

// PATCH /cards/:cardId/remove-row
export const removeCardRowApi = (identity: PCChatbotIdentity, cardId: string, rowId: string) =>
    pcChatbotFetch<PCCardResponse>(`/cards/${cardId}/remove-row`, {
        method: 'PATCH',
        identity,
        body: { rowId },
    });

/* ─────────────────────────── Channels (§4.8) ─────────────────────────── */

// GET /channels
export const fetchChannelsApi = (identity: PCChatbotIdentity) =>
    pcChatbotFetch<PCChannelsResponse>('/channels', { method: 'GET', identity });

/* ─────────────────────────── Sync (§4.9 / §7) ─────────────────────────── */

// POST /conversations/:id/sync — wire response is { card: PCSyncCard }, not the bare card.
// `msgId` is the sync card's stream message id, sent so the backend can persist the sync's
// running/results state back onto that message and have it survive a page reload (§Item 2).
export const runSyncApi = (identity: PCChatbotIdentity, conversationId: string, msgId?: string) =>
    pcChatbotFetch<PCSyncCardResponse>(`/conversations/${conversationId}/sync`, { method: 'POST', identity, body: { msgId } });

// POST /conversations/:id/sync/results
export const fetchSyncResultsApi = (identity: PCChatbotIdentity, conversationId: string, msgId?: string) =>
    pcChatbotFetch<PCSyncCardResponse>(`/conversations/${conversationId}/sync/results`, { method: 'POST', identity, body: { msgId } });

// POST /conversations/:id/sync/retry
export const retrySyncApi = (identity: PCChatbotIdentity, conversationId: string, previous: PCSyncResultRow[], msgId?: string) =>
    pcChatbotFetch<PCSyncCardResponse>(`/conversations/${conversationId}/sync/retry`, {
        method: 'POST',
        identity,
        body: { previous, msgId },
    });

/* ─────────────────────────── Activity log & undo (§4.10 / §8) ─────────────────────────── */

// GET /log
export const fetchLogApi = (identity: PCChatbotIdentity) =>
    pcChatbotFetch<PCLogResponse>('/log', { method: 'GET', identity });

// POST /log/:entryId/undo
// `conversationId` = the latest/active thread. The backend appends the reversal's messages to
// it and echoes the id back, so the UI can route the user there once the undo succeeds.
export const undoLogEntryApi = (identity: PCChatbotIdentity, entryId: string, conversationId?: string | null) =>
    pcChatbotFetch<PCUndoResponse>(`/log/${entryId}/undo`, {
        method: 'POST',
        identity,
        body: conversationId ? { conversationId } : {},
    });

/* ─────────────────────────── Quick-action CTAs (§Item 6) ─────────────────────────── */

// GET /cta — the backend-owned quick actions shown on the intro screen.
export const fetchCtasApi = (identity: PCChatbotIdentity) =>
    pcChatbotFetch<PCCtaResponse>('/cta', { method: 'GET', identity });

/* ─────────────────────────── Manual refresh (§4.11) ─────────────────────────── */

// POST /catalogue/refresh
export const refreshCatalogueApi = (identity: PCChatbotIdentity) =>
    pcChatbotFetch<PCCatalogueRefreshResponse>('/catalogue/refresh', { method: 'POST', identity, body: {} });
